import React, { useRef, useEffect, useCallback } from "react";

interface DataPoint {
	t: string;
	v: string;
}

interface HoverData {
	level: number;
	time: number;
}

interface TideChartProps {
	waterLevels: DataPoint[];
	predictions: DataPoint[];
	hiLo: Array<DataPoint & { type: string }>;
	darkMode?: boolean;
	onHover?: (data: HoverData | null) => void;
}

interface ParsedPoint {
	t: number;
	v: number;
}

function parseTime(t: string): number {
	return new Date(t).getTime();
}

function interpolateValue(points: ParsedPoint[], targetT: number): number | null {
	if (points.length === 0) return null;
	if (targetT < points[0].t || targetT > points[points.length - 1].t) return null;

	for (let i = 0; i < points.length - 1; i++) {
		if (targetT >= points[i].t && targetT <= points[i + 1].t) {
			const ratio = (targetT - points[i].t) / (points[i + 1].t - points[i].t);
			return points[i].v + ratio * (points[i + 1].v - points[i].v);
		}
	}
	return null;
}

export function TideChart({ waterLevels, predictions, hiLo, darkMode, onHover }: TideChartProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const overlayRef = useRef<HTMLCanvasElement>(null);
	const onHoverRef = useRef(onHover);
	onHoverRef.current = onHover;
	const chartDataRef = useRef<{
		minT: number;
		maxT: number;
		minV: number;
		maxV: number;
		PADDING: { top: number; right: number; bottom: number; left: number };
		plotW: number;
		plotH: number;
		W: number;
		H: number;
		obsPoints: ParsedPoint[];
		predPoints: ParsedPoint[];
	} | null>(null);

	const drawChart = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const dpr = window.devicePixelRatio || 1;
		const rect = canvas.getBoundingClientRect();
		canvas.width = rect.width * dpr;
		canvas.height = rect.height * dpr;
		ctx.scale(dpr, dpr);

		// Also size the overlay canvas
		const overlay = overlayRef.current;
		if (overlay) {
			overlay.width = rect.width * dpr;
			overlay.height = rect.height * dpr;
		}

		const W = rect.width;
		const H = rect.height;
		const PADDING = { top: 30, right: 20, bottom: 50, left: 55 };
		const plotW = W - PADDING.left - PADDING.right;
		const plotH = H - PADDING.top - PADDING.bottom;

		// Combine all data to find ranges
		const allPoints = [
			...waterLevels.map((p) => ({ t: parseTime(p.t), v: parseFloat(p.v) })),
			...predictions.map((p) => ({ t: parseTime(p.t), v: parseFloat(p.v) })),
		].filter((p) => !isNaN(p.v) && !isNaN(p.t));

		if (allPoints.length === 0) return;

		const times = allPoints.map((p) => p.t);
		const values = allPoints.map((p) => p.v);
		const minT = Math.min(...times);
		const maxT = Math.max(...times);
		const minV = Math.min(...values) - 0.5;
		const maxV = Math.max(...values) + 0.5;

		const obsPoints = waterLevels
			.map((p) => ({ t: parseTime(p.t), v: parseFloat(p.v) }))
			.filter((p) => !isNaN(p.v));

		const predPoints = predictions
			.map((p) => ({ t: parseTime(p.t), v: parseFloat(p.v) }))
			.filter((p) => !isNaN(p.v));

		// Store chart data for hover calculations
		chartDataRef.current = { minT, maxT, minV, maxV, PADDING, plotW, plotH, W, H, obsPoints, predPoints };

		const scaleX = (t: number) => PADDING.left + ((t - minT) / (maxT - minT)) * plotW;
		const scaleY = (v: number) => PADDING.top + plotH - ((v - minV) / (maxV - minV)) * plotH;

		// Get computed styles for theming
		const styles = getComputedStyle(document.documentElement);
		const fg = styles.getPropertyValue("--foreground").trim();
		const mutedFg = styles.getPropertyValue("--muted-foreground").trim();
		const border = styles.getPropertyValue("--border").trim();
		const bg = styles.getPropertyValue("--background").trim();

		// Clear
		ctx.clearRect(0, 0, W, H);

		// Grid lines
		ctx.strokeStyle = border || "hsl(240 5.9% 90%)";
		ctx.lineWidth = 0.5;
		const numYLines = 6;
		for (let i = 0; i <= numYLines; i++) {
			const v = minV + (i / numYLines) * (maxV - minV);
			const y = scaleY(v);
			ctx.beginPath();
			ctx.moveTo(PADDING.left, y);
			ctx.lineTo(W - PADDING.right, y);
			ctx.stroke();

			// Y-axis labels
			ctx.fillStyle = mutedFg || "hsl(240 3.8% 46.1%)";
			ctx.font = "11px system-ui, sans-serif";
			ctx.textAlign = "right";
			ctx.textBaseline = "middle";
			ctx.fillText(`${v.toFixed(1)} ft`, PADDING.left - 8, y);
		}

		// X-axis time labels
		const timeSpan = maxT - minT;
		const hourMs = 60 * 60 * 1000;
		const hourInterval = timeSpan > 48 * hourMs ? 12 : timeSpan > 24 * hourMs ? 6 : 3;

		const firstHour = new Date(minT);
		firstHour.setMinutes(0, 0, 0);
		let cursor = firstHour.getTime();

		ctx.fillStyle = mutedFg || "hsl(240 3.8% 46.1%)";
		ctx.font = "11px system-ui, sans-serif";
		ctx.textAlign = "center";
		ctx.textBaseline = "top";

		while (cursor <= maxT) {
			if (cursor >= minT) {
				const x = scaleX(cursor);
				const d = new Date(cursor);
				const hours = d.getHours();
				const ampm = hours >= 12 ? "PM" : "AM";
				const h12 = hours % 12 || 12;

				// Vertical grid
				ctx.strokeStyle = border || "hsl(240 5.9% 90%)";
				ctx.lineWidth = 0.5;
				ctx.beginPath();
				ctx.moveTo(x, PADDING.top);
				ctx.lineTo(x, PADDING.top + plotH);
				ctx.stroke();

				ctx.fillStyle = mutedFg || "hsl(240 3.8% 46.1%)";
				ctx.fillText(`${h12}${ampm}`, x, PADDING.top + plotH + 8);

				// Show date at midnight
				if (hours === 0) {
					const dateStr = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
					ctx.fillText(dateStr, x, PADDING.top + plotH + 24);
				}
			}
			cursor += hourInterval * hourMs;
		}

		// Draw "now" line
		const now = Date.now();
		if (now >= minT && now <= maxT) {
			const nowX = scaleX(now);
			ctx.strokeStyle = "hsl(0 84.2% 60.2%)";
			ctx.lineWidth = 1.5;
			ctx.setLineDash([4, 4]);
			ctx.beginPath();
			ctx.moveTo(nowX, PADDING.top);
			ctx.lineTo(nowX, PADDING.top + plotH);
			ctx.stroke();
			ctx.setLineDash([]);

			ctx.fillStyle = "hsl(0 84.2% 60.2%)";
			ctx.font = "bold 10px system-ui, sans-serif";
			ctx.textAlign = "center";
			ctx.fillText("NOW", nowX, PADDING.top - 10);
		}

		// Draw predictions (forecast line)
		if (predPoints.length > 1) {
			ctx.strokeStyle = "hsl(200 80% 60%)";
			ctx.lineWidth = 2;
			ctx.globalAlpha = 0.5;
			ctx.beginPath();
			predPoints.forEach((p, i) => {
				const x = scaleX(p.t);
				const y = scaleY(p.v);
				if (i === 0) ctx.moveTo(x, y);
				else ctx.lineTo(x, y);
			});
			ctx.stroke();

			// Fill under prediction curve
			ctx.globalAlpha = 0.05;
			ctx.fillStyle = "hsl(200 80% 60%)";
			ctx.lineTo(scaleX(predPoints[predPoints.length - 1].t), PADDING.top + plotH);
			ctx.lineTo(scaleX(predPoints[0].t), PADDING.top + plotH);
			ctx.closePath();
			ctx.fill();
			ctx.globalAlpha = 1;
		}

		// Draw observed water levels
		if (obsPoints.length > 1) {
			ctx.strokeStyle = "hsl(173 58% 39%)";
			ctx.lineWidth = 2.5;
			ctx.beginPath();
			obsPoints.forEach((p, i) => {
				const x = scaleX(p.t);
				const y = scaleY(p.v);
				if (i === 0) ctx.moveTo(x, y);
				else ctx.lineTo(x, y);
			});
			ctx.stroke();

			// Fill under observed curve
			ctx.globalAlpha = 0.08;
			ctx.fillStyle = "hsl(173 58% 39%)";
			ctx.lineTo(scaleX(obsPoints[obsPoints.length - 1].t), PADDING.top + plotH);
			ctx.lineTo(scaleX(obsPoints[0].t), PADDING.top + plotH);
			ctx.closePath();
			ctx.fill();
			ctx.globalAlpha = 1;
		}

		// Draw hi/lo markers
		hiLo.forEach((p) => {
			const t = parseTime(p.t);
			const v = parseFloat(p.v);
			if (isNaN(v) || t < minT || t > maxT) return;

			const x = scaleX(t);
			const y = scaleY(v);
			const isHigh = p.type === "H";

			ctx.beginPath();
			ctx.arc(x, y, 4, 0, Math.PI * 2);
			ctx.fillStyle = isHigh ? "hsl(200 80% 50%)" : "hsl(30 80% 55%)";
			ctx.fill();
			ctx.strokeStyle = bg || "white";
			ctx.lineWidth = 1.5;
			ctx.stroke();

			// Label
			ctx.fillStyle = fg || "hsl(240 10% 3.9%)";
			ctx.font = "bold 10px system-ui, sans-serif";
			ctx.textAlign = "center";
			const label = `${isHigh ? "H" : "L"} ${v.toFixed(1)}ft`;
			const timeLabel = new Date(t).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
			ctx.fillText(label, x, y + (isHigh ? -14 : 14));
			ctx.font = "10px system-ui, sans-serif";
			ctx.fillStyle = mutedFg || "hsl(240 3.8% 46.1%)";
			ctx.fillText(timeLabel, x, y + (isHigh ? -25 : 25));
		});

		// Legend
		const legendY = PADDING.top + plotH + 35;
		ctx.font = "11px system-ui, sans-serif";

		// Observed
		ctx.fillStyle = "hsl(173 58% 39%)";
		ctx.fillRect(PADDING.left, legendY, 14, 3);
		ctx.fillStyle = mutedFg || "hsl(240 3.8% 46.1%)";
		ctx.textAlign = "left";
		ctx.fillText("Observed", PADDING.left + 20, legendY + 3);

		// Predicted
		ctx.fillStyle = "hsl(200 80% 60%)";
		ctx.fillRect(PADDING.left + 100, legendY, 14, 3);
		ctx.fillStyle = mutedFg || "hsl(240 3.8% 46.1%)";
		ctx.fillText("Forecast", PADDING.left + 120, legendY + 3);
	}, [waterLevels, predictions, hiLo, darkMode]);

	useEffect(() => {
		drawChart();
	}, [drawChart]);

	// Hover interaction on overlay canvas
	useEffect(() => {
		const overlay = overlayRef.current;
		if (!overlay) return;

		const dpr = window.devicePixelRatio || 1;

		function drawOverlay(mouseX: number) {
			const data = chartDataRef.current;
			if (!data || !overlay) return;

			const ctx = overlay.getContext("2d");
			if (!ctx) return;

			ctx.save();
			ctx.scale(dpr, dpr);
			ctx.clearRect(0, 0, data.W, data.H);

			const { minT, maxT, minV, maxV, PADDING, plotW, plotH, obsPoints, predPoints } = data;

			// Check if mouse is in plot area
			if (mouseX < PADDING.left || mouseX > PADDING.left + plotW) {
				ctx.restore();
				return;
			}

			// Read theme colors from CSS variables
			const styles = getComputedStyle(document.documentElement);
			const isDark = document.documentElement.classList.contains("dark");
			const popoverBg = styles.getPropertyValue("--popover").trim() || (isDark ? "hsl(240 10% 3.9%)" : "hsl(0 0% 100%)");
			const borderColor = styles.getPropertyValue("--border").trim() || (isDark ? "hsl(240 3.7% 15.9%)" : "hsl(240 5.9% 90%)");
			const mutedFg = styles.getPropertyValue("--muted-foreground").trim() || (isDark ? "hsl(240 5% 64.9%)" : "hsl(240 3.8% 46.1%)");

			const scaleY = (v: number) => PADDING.top + plotH - ((v - minV) / (maxV - minV)) * plotH;

			// Convert mouse X to time
			const hoverT = minT + ((mouseX - PADDING.left) / plotW) * (maxT - minT);

			// Interpolate values
			const obsVal = interpolateValue(obsPoints, hoverT);
			const predVal = interpolateValue(predPoints, hoverT);

			if (obsVal === null && predVal === null) {
				onHoverRef.current?.(null);
				ctx.restore();
				return;
			}

			// Notify parent of hovered level
			const hoverLevel = obsVal ?? predVal!;
			onHoverRef.current?.({ level: hoverLevel, time: hoverT });

			// Draw vertical crosshair line
			ctx.strokeStyle = mutedFg;
			ctx.globalAlpha = 0.5;
			ctx.lineWidth = 1;
			ctx.setLineDash([3, 3]);
			ctx.beginPath();
			ctx.moveTo(mouseX, PADDING.top);
			ctx.lineTo(mouseX, PADDING.top + plotH);
			ctx.stroke();
			ctx.setLineDash([]);
			ctx.globalAlpha = 1;

			// Draw dots on the lines
			if (obsVal !== null) {
				const y = scaleY(obsVal);
				ctx.beginPath();
				ctx.arc(mouseX, y, 5, 0, Math.PI * 2);
				ctx.fillStyle = "hsl(173 58% 39%)";
				ctx.fill();
				ctx.strokeStyle = isDark ? "hsl(240 10% 3.9%)" : "white";
				ctx.lineWidth = 2;
				ctx.stroke();
			}

			if (predVal !== null) {
				const y = scaleY(predVal);
				ctx.beginPath();
				ctx.arc(mouseX, y, 5, 0, Math.PI * 2);
				ctx.fillStyle = "hsl(200 80% 60%)";
				ctx.fill();
				ctx.strokeStyle = isDark ? "hsl(240 10% 3.9%)" : "white";
				ctx.lineWidth = 2;
				ctx.stroke();
			}

			// Draw tooltip
			const hoverDate = new Date(hoverT);
			const timeStr = hoverDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
			const dateStr = hoverDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });

			const lines: string[] = [`${dateStr}, ${timeStr}`];
			if (obsVal !== null) lines.push(`Observed: ${obsVal.toFixed(2)} ft`);
			if (predVal !== null) lines.push(`Forecast: ${predVal.toFixed(2)} ft`);

			ctx.font = "12px system-ui, sans-serif";
			const lineHeight = 18;
			const tooltipPadX = 10;
			const tooltipPadY = 8;
			const maxTextW = Math.max(...lines.map((l) => ctx.measureText(l).width));
			const tooltipW = maxTextW + tooltipPadX * 2;
			const tooltipH = lines.length * lineHeight + tooltipPadY * 2;

			// Position tooltip - flip side if near edge
			let tooltipX = mouseX + 12;
			if (tooltipX + tooltipW > PADDING.left + plotW) {
				tooltipX = mouseX - tooltipW - 12;
			}
			let tooltipY = PADDING.top + 10;

			// Background - use theme popover colors
			ctx.globalAlpha = 0.95;
			ctx.fillStyle = popoverBg;
			ctx.strokeStyle = borderColor;
			ctx.lineWidth = 1;
			const r = 6;
			ctx.beginPath();
			ctx.moveTo(tooltipX + r, tooltipY);
			ctx.lineTo(tooltipX + tooltipW - r, tooltipY);
			ctx.arcTo(tooltipX + tooltipW, tooltipY, tooltipX + tooltipW, tooltipY + r, r);
			ctx.lineTo(tooltipX + tooltipW, tooltipY + tooltipH - r);
			ctx.arcTo(tooltipX + tooltipW, tooltipY + tooltipH, tooltipX + tooltipW - r, tooltipY + tooltipH, r);
			ctx.lineTo(tooltipX + r, tooltipY + tooltipH);
			ctx.arcTo(tooltipX, tooltipY + tooltipH, tooltipX, tooltipY + tooltipH - r, r);
			ctx.lineTo(tooltipX, tooltipY + r);
			ctx.arcTo(tooltipX, tooltipY, tooltipX + r, tooltipY, r);
			ctx.closePath();
			ctx.fill();
			ctx.globalAlpha = 1;
			ctx.stroke();

			// Text
			ctx.textAlign = "left";
			ctx.textBaseline = "top";
			lines.forEach((line, i) => {
				if (i === 0) {
					ctx.fillStyle = mutedFg;
					ctx.font = "bold 11px system-ui, sans-serif";
				} else if (line.startsWith("Observed")) {
					ctx.fillStyle = isDark ? "hsl(173 58% 50%)" : "hsl(173 58% 32%)";
					ctx.font = "12px system-ui, sans-serif";
				} else {
					ctx.fillStyle = isDark ? "hsl(200 80% 65%)" : "hsl(200 80% 45%)";
					ctx.font = "12px system-ui, sans-serif";
				}
				ctx.fillText(line, tooltipX + tooltipPadX, tooltipY + tooltipPadY + i * lineHeight);
			});

			ctx.restore();
		}

		function clearOverlay() {
			if (!overlay) return;
			const ctx = overlay.getContext("2d");
			if (!ctx) return;
			const data = chartDataRef.current;
			if (!data) return;
			ctx.clearRect(0, 0, overlay.width, overlay.height);
			onHoverRef.current?.(null);
		}

		function handleMouseMove(e: MouseEvent) {
			const rect = overlay.getBoundingClientRect();
			const mouseX = e.clientX - rect.left;
			drawOverlay(mouseX);
		}

		function handleMouseLeave() {
			clearOverlay();
		}

		function handleTouchMove(e: TouchEvent) {
			if (e.touches.length === 1) {
				e.preventDefault();
				const rect = overlay.getBoundingClientRect();
				const mouseX = e.touches[0].clientX - rect.left;
				drawOverlay(mouseX);
			}
		}

		function handleTouchEnd() {
			clearOverlay();
		}

		overlay.addEventListener("mousemove", handleMouseMove);
		overlay.addEventListener("mouseleave", handleMouseLeave);
		overlay.addEventListener("touchmove", handleTouchMove, { passive: false });
		overlay.addEventListener("touchend", handleTouchEnd);

		return () => {
			overlay.removeEventListener("mousemove", handleMouseMove);
			overlay.removeEventListener("mouseleave", handleMouseLeave);
			overlay.removeEventListener("touchmove", handleTouchMove);
			overlay.removeEventListener("touchend", handleTouchEnd);
		};
	}, [waterLevels, predictions, hiLo]);

	return (
		<div className="relative w-full h-full">
			<canvas
				ref={canvasRef}
				className="w-full h-full"
				style={{ width: "100%", height: "100%" }}
			/>
			<canvas
				ref={overlayRef}
				className="absolute inset-0 w-full h-full"
				style={{ width: "100%", height: "100%", cursor: "crosshair" }}
			/>
		</div>
	);
}
